/**
 * Where the tables stand in a hedge maze: one in a dead end, facing the way you came in.
 *
 * A profile says how many; this says which cul-de-sacs get one, drawn from the level's own
 * seed so the answer is the same on every device and every launch (architectural rule 4).
 * Hand-authoring a position would not survive a seed change anyway — the dead end it stood
 * in would have moved.
 *
 * Unlike `placeLadders` there is no spacing pass. Dead ends are already spread through the
 * maze by construction, and there are far fewer of them than there are hedge faces, so a
 * plain shuffle spreads tables well enough; a spacing rule would only make a small maze
 * quietly place fewer tables than it was asked for.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';
import type { GroundPoint, Vec3 } from '../profile/types';
import { CELL_SIZE } from './grid';
import { deadEnds } from './deadEnds';
import type { Maze } from './generateMaze';

export type Table = {
  /** Centre of the table in plan; it stands on the ground. */
  readonly center: GroundPoint;
  /** Rotation about Y, radians, such that the table's front faces the dead end's mouth. */
  readonly yaw: number;
  /** Full extents. Every table is the same. */
  readonly size: Vec3;
  /** Ground to table top — where anything resting on it sits. */
  readonly topHeight: number;
};

/**
 * A kitchen table: waist-high, and small enough to leave 0.6 m either side of it in a
 * 2.4 m corridor so a player can squeeze past rather than being walled in by it.
 *
 * The height is the load-bearing number. `MAX_STEP_HEIGHT` is 0.4 m, so the character
 * controller will not autostep up a table; the jump reaches about 0.9 m, so a player can
 * get on top of one. Anything between those two is a table you climb deliberately.
 */
export const TABLE_SIZE: Vec3 = { x: 1.2, y: 0.75, z: 0.8 };

/**
 * `count` tables, in dead ends drawn from the seed — or as many as the maze has dead ends,
 * which is the same "as many as will fit" contract `placeLadders` keeps. `validate.ts`
 * refuses a profile that asks for more, so reaching the shorter list here means the maze
 * changed under a profile that no longer matches it.
 */
export function placeTables(maze: Maze, seed: string, count: number): Table[] {
  if (count <= 0) return [];

  const candidates = deadEnds(maze);
  // Namespaced like `generateMaze`'s 'maze:' draw and `placeLadders`' 'ladders:', so one
  // profile seed feeds all three without them inheriting each other's stream.
  const random = mulberry32(seedFromString(`tables:${seed}`));
  for (let i = candidates.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j]!, candidates[i]!];
  }

  const mazeWidth = maze.columns * CELL_SIZE;
  const mazeDepth = maze.rows * CELL_SIZE;

  return candidates.slice(0, count).map((cell) => {
    const center: GroundPoint = {
      x: -mazeWidth / 2 + cell.x * CELL_SIZE + CELL_SIZE / 2,
      z: -mazeDepth / 2 + cell.z * CELL_SIZE + CELL_SIZE / 2,
    };
    return {
      center,
      // Local +z points at the opening, so a table met head-on is met across its long side.
      yaw: Math.atan2(cell.opensTo.x, cell.opensTo.z),
      size: TABLE_SIZE,
      topHeight: TABLE_SIZE.y,
    };
  });
}
